// Recorder modal.
//
// Full-takeover dialog with three working modes:
//
//   1. "Build manually"  — form-based step builder. Always works; no
//                          operator setup required. Users define each step
//                          (action + selector + value + semantic label) and
//                          reorder/edit before saving as a workflow.
//
//   2. "Record in browser" — slot for the noVNC iframe stream. Shows a
//                          friendly waiting state until the worker host
//                          is set up. Steps captured in this mode flow
//                          through the recorder API on the backend.
//
//   3. "Edit JSON"        — power-user mode. Edit steps_json directly.
//                          Validates structure on save.
//
// Save → calls POST /finance/rpa/workflows with the assembled steps_json.
// (We use the standard workflow CRUD path rather than the recorder finalize
//  endpoint; both end up at the same SQL row.)
//
// Discard → simply closes the modal. Manual builder doesn't create any
// server-side state until Save is clicked, so there's nothing to clean up.
import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
    Box, Stack, Paper, Typography, IconButton, Button, Chip, Alert, Divider,
    Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, MenuItem, Select, FormControl, InputLabel, Tooltip,
    Tabs, Tab, LinearProgress, List, ListItem, ListItemText, ListItemIcon,
    CircularProgress, Portal, AppBar, Toolbar, Snackbar,
    useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import SaveIcon from '@mui/icons-material/Save';
import OpenInBrowserIcon from '@mui/icons-material/OpenInBrowser';
import CodeIcon from '@mui/icons-material/Code';
import BuildIcon from '@mui/icons-material/Build';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import KeyboardIcon from '@mui/icons-material/Keyboard';
import DownloadIcon from '@mui/icons-material/Download';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import PublicIcon from '@mui/icons-material/Public';
import EventIcon from '@mui/icons-material/Event';
import axios from '../../../utils/axiosInstance';
import {
    rpaWorkflowCreate,
    rpaRecordingStart, rpaRecordingSteps, rpaRecordingStatus,
    rpaRecordingStop, rpaRecordingFinalize, rpaRecordingDiscard,
    rpaRecordingFrameUrl, rpaRecordingInput,
    rpaDateFilterPresets, rpaDateFilterPreview,
} from './api';


const ACTIONS = [
    { value: 'navigate', label: 'Navigate to URL', icon: <PublicIcon fontSize="small" /> },
    { value: 'click',    label: 'Click element',    icon: <ArrowForwardIosIcon fontSize="small" /> },
    { value: 'type',     label: 'Type into field',  icon: <KeyboardIcon fontSize="small" /> },
    { value: 'select',   label: 'Select dropdown',  icon: <ArrowForwardIosIcon fontSize="small" /> },
    { value: 'wait',     label: 'Wait',             icon: <HourglassBottomIcon fontSize="small" /> },
    { value: 'download', label: 'Click + download', icon: <DownloadIcon fontSize="small" /> },
];

const ACTION_LABEL = Object.fromEntries(ACTIONS.map((a) => [a.value, a.label]));
const ACTION_ICON = Object.fromEntries(ACTIONS.map((a) => [a.value, a.icon]));

const EMPTY_STEP = {
    action: 'navigate',
    selector: { primary: '', xpath: '', text: '' },
    value: '',
    semantic_label: '',
};


export default function RecorderModal({
    open, onClose, merchants, defaultMerchantId, seedSteps, onSaved,
}) {
    const [merchantId, setMerchantId] = useState(defaultMerchantId || '');
    const [name, setName] = useState('');
    const [paramsSchemaText, setParamsSchemaText] = useState('');
    const [steps, setSteps] = useState([]);
    const [mode, setMode] = useState(0);   // 0 browser, 1 manual, 2 JSON
    const [editingIdx, setEditingIdx] = useState(null);   // null = closed; -1 = new step
    const [draft, setDraft] = useState(EMPTY_STEP);
    const [jsonText, setJsonText] = useState('[]');
    const [error, setError] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    // Reset state every time the modal opens. If seedSteps was provided
    // (duplicate-workflow flow), normalise them into the editor's shape.
    useEffect(() => {
        if (!open) return;
        // Pick the initial merchant:
        //   1. defaultMerchantId from the caller (e.g. duplicate-workflow flow)
        //   2. else, if there's exactly ONE active merchant available,
        //      auto-select it — saves the user a click in the common
        //      "I only have one merchant" case
        //   3. else, blank — user picks from the dropdown
        let initialMerchant = defaultMerchantId || '';
        if (!initialMerchant && Array.isArray(merchants) && merchants.length > 0) {
            const active = merchants.filter((m) => m.is_active !== false);
            if (active.length === 1) {
                initialMerchant = active[0].id;
            }
        }
        setMerchantId(initialMerchant);
        setName('');
        setParamsSchemaText('');
        const seeded = Array.isArray(seedSteps) ? seedSteps.map(normaliseSeedStep) : [];
        setSteps(seeded);
        setMode(0);
        setEditingIdx(null);
        setDraft(EMPTY_STEP);
        setJsonText(JSON.stringify(stepsForSave(seeded), null, 2));
        setError(null);
        setSubmitting(false);
    }, [open, defaultMerchantId, seedSteps, merchants]);

    // Keep JSON tab in sync when user switches into it.
    useEffect(() => {
        if (mode === 2) setJsonText(JSON.stringify(stepsForSave(steps), null, 2));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode]);

    const merchant = useMemo(
        () => merchants.find((m) => m.id === merchantId),
        [merchants, merchantId],
    );

    const startNew = () => {
        setDraft({ ...EMPTY_STEP, step_idx: steps.length });
        setEditingIdx(-1);
    };

    const startEdit = (idx) => {
        setDraft({ ...steps[idx] });
        setEditingIdx(idx);
    };

    const commitDraft = () => {
        if (!validateDraft(draft, setError)) return;
        const next = [...steps];
        if (editingIdx === -1) next.push(draft);
        else next[editingIdx] = draft;
        // Re-index to keep step_idx monotonic.
        setSteps(next.map((s, i) => ({ ...s, step_idx: i })));
        setEditingIdx(null);
        setDraft(EMPTY_STEP);
        setError(null);
    };

    const cancelDraft = () => { setEditingIdx(null); setDraft(EMPTY_STEP); setError(null); };

    const removeStep = (idx) => {
        const next = steps.filter((_, i) => i !== idx).map((s, i) => ({ ...s, step_idx: i }));
        setSteps(next);
    };

    const moveStep = (idx, delta) => {
        const newIdx = idx + delta;
        if (newIdx < 0 || newIdx >= steps.length) return;
        const next = [...steps];
        [next[idx], next[newIdx]] = [next[newIdx], next[idx]];
        setSteps(next.map((s, i) => ({ ...s, step_idx: i })));
    };

    const onSave = async () => {
        setError(null);

        if (!merchantId) { setError('Pick a merchant.'); return; }
        if (!name.trim()) { setError('Workflow name is required.'); return; }

        let stepsToSave;
        if (mode === 2) {
            try {
                stepsToSave = JSON.parse(jsonText);
                if (!Array.isArray(stepsToSave)) throw new Error('Steps must be an array.');
            } catch (e) {
                setError(`Steps JSON is invalid: ${e.message}`);
                return;
            }
        } else {
            stepsToSave = stepsForSave(steps);
        }

        if (stepsToSave.length === 0) {
            setError('Add at least one step before saving.');
            return;
        }

        let paramsSchema = null;
        if (paramsSchemaText.trim()) {
            try { paramsSchema = JSON.parse(paramsSchemaText); }
            catch { setError('Params schema must be valid JSON or empty.'); return; }
        }

        setSubmitting(true);
        try {
            const r = await rpaWorkflowCreate({
                merchant_id: merchantId,
                name: name.trim(),
                steps_json: stepsToSave,
                params_schema: paramsSchema,
            });
            if (!r?.data?.success) throw new Error(r?.data?.message || 'Save failed');
            const wf = r.data.data;
            onSaved?.(wf);
            onClose();
        } catch (e) {
            setError(e?.response?.data?.message || e.message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog
            open={open}
            // Block backdrop clicks / Esc to prevent accidental close,
            // but ALWAYS allow the X button (handled below). Even
            // during a live recording the user can always escape — if
            // the backend somehow gets stuck reporting 'stopping' the
            // user is no longer trapped staring at a locked modal.
            onClose={(_e, reason) => {
                if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
                onClose();
            }}
            fullScreen
        >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1.5 }}>
                <Chip color="error" label="REC" size="small" sx={{ fontWeight: 700 }} />
                <Typography variant="h6" sx={{ flex: 1 }}>Build / Record Workflow</Typography>
                <IconButton
                    onClick={onClose}
                    disabled={submitting}
                    title="Close"
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <Divider />

            <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
                {error && <Alert severity="error" sx={{ m: 2 }} onClose={() => setError(null)}>{error}</Alert>}

                {/* Header form: merchant + workflow name + params schema */}
                <Paper variant="outlined" sx={{ m: 2, p: 2 }}>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                        <FormControl size="small" sx={{ minWidth: 220 }}>
                            <InputLabel>Merchant *</InputLabel>
                            <Select label="Merchant *" value={merchantId}
                                    onChange={(e) => setMerchantId(e.target.value)}>
                                {merchants.map((m) => (
                                    <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            size="small"
                            label="Workflow name *"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="e.g. Monthly Invoice Pull"
                            sx={{ flex: 1 }}
                        />
                        <TextField
                            size="small"
                            label='Params schema (JSON, optional)'
                            value={paramsSchemaText}
                            onChange={(e) => setParamsSchemaText(e.target.value)}
                            placeholder='{ "period_start": "date" }'
                            sx={{ flex: 1, fontFamily: 'monospace' }}
                        />
                    </Stack>
                    {merchant && !merchant.credential_meta?.has_password && (
                        <Alert severity="warning" sx={{ mt: 2 }}>
                            This merchant has no stored password. Replays will skip login —
                            either set credentials in the Merchants tab, or use only
                            navigate/wait actions.
                        </Alert>
                    )}
                </Paper>

                <Tabs
                    value={mode}
                    onChange={(_, v) => setMode(v)}
                    sx={{ px: 2 }}
                >
                    {/* "Record in browser" is the primary path now —
                        first tab, default selection on modal open. */}
                    <Tab icon={<OpenInBrowserIcon />} iconPosition="start" label="Record in browser" />
                    <Tab icon={<BuildIcon />} iconPosition="start" label="Build manually" />
                    <Tab icon={<CodeIcon />} iconPosition="start" label="Edit JSON" />
                </Tabs>
                <Divider />

                <Box sx={{ flex: 1, p: 2, overflow: 'auto' }}>
                    {mode === 0 && (
                        <BrowserRecorder
                            merchantId={merchantId}
                            workflowName={name}
                            paramsSchemaText={paramsSchemaText}
                            onSavedToDashboardTab={() => {
                                // After in-browser save, close the modal —
                                // finalize publishes the workflow itself.
                                onClose();
                            }}
                        />
                    )}
                    {mode === 1 && (
                        <ManualBuilder
                            steps={steps}
                            onAdd={startNew}
                            onEdit={startEdit}
                            onRemove={removeStep}
                            onMove={moveStep}
                        />
                    )}
                    {mode === 2 && (
                        <TextField
                            label="steps_json (array)"
                            multiline
                            minRows={20}
                            fullWidth
                            value={jsonText}
                            onChange={(e) => setJsonText(e.target.value)}
                            sx={{ fontFamily: 'monospace' }}
                        />
                    )}
                </Box>
            </DialogContent>

            {/* Step editor sub-dialog */}
            <StepEditor
                open={editingIdx !== null}
                draft={draft}
                onChange={setDraft}
                onCommit={commitDraft}
                onCancel={cancelDraft}
                isNew={editingIdx === -1}
            />
        </Dialog>
    );
}


// ---------------------------------------------------------------------------
// Manual builder column
// ---------------------------------------------------------------------------

function ManualBuilder({ steps, onAdd, onEdit, onRemove, onMove }) {
    return (
        <Stack spacing={1}>
            <Stack direction="row" alignItems="center">
                <Typography variant="subtitle1" sx={{ flex: 1 }}>
                    Steps ({steps.length})
                </Typography>
                <Button startIcon={<AddIcon />} variant="contained" size="small" onClick={onAdd}>
                    Add step
                </Button>
            </Stack>

            {steps.length === 0 && (
                <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                        No steps yet. Click <strong>Add step</strong> to get started — try
                        Navigate → Type (username) → Type (password) → Click (login)
                        → Navigate (invoices) → Click (download).
                    </Typography>
                </Paper>
            )}

            <List disablePadding>
                {steps.map((s, idx) => (
                    <Paper key={idx} variant="outlined" sx={{ mb: 1 }}>
                        <ListItem
                            secondaryAction={
                                <Stack direction="row" spacing={0.5}>
                                    <Tooltip title="Move up">
                                        <span>
                                            <IconButton size="small" onClick={() => onMove(idx, -1)} disabled={idx === 0}>
                                                <ArrowUpwardIcon fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                    <Tooltip title="Move down">
                                        <span>
                                            <IconButton size="small" onClick={() => onMove(idx, 1)} disabled={idx === steps.length - 1}>
                                                <ArrowDownwardIcon fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                    <Tooltip title="Edit">
                                        <IconButton size="small" onClick={() => onEdit(idx)}>
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Delete">
                                        <IconButton size="small" color="error" onClick={() => onRemove(idx)}>
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Stack>
                            }
                        >
                            <ListItemIcon>
                                <Chip size="small" label={`#${idx + 1}`} sx={{ minWidth: 36 }} />
                            </ListItemIcon>
                            <ListItemText
                                primary={
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        {ACTION_ICON[s.action]}
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            {ACTION_LABEL[s.action] || s.action}
                                        </Typography>
                                        {s.semantic_label && (
                                            <Chip label={s.semantic_label} size="small" variant="outlined" />
                                        )}
                                    </Stack>
                                }
                                secondary={
                                    <Box sx={{ fontFamily: 'monospace', fontSize: 12,
                                                color: 'text.secondary', mt: 0.5 }}>
                                        {s.selector?.primary && <div>selector: {s.selector.primary}</div>}
                                        {s.value && <div>value: {redactPwd(s.value)}</div>}
                                    </Box>
                                }
                            />
                        </ListItem>
                    </Paper>
                ))}
            </List>
        </Stack>
    );
}


// ---------------------------------------------------------------------------
// Step editor sub-dialog
// ---------------------------------------------------------------------------

function StepEditor({ open, draft, onChange, onCommit, onCancel, isNew }) {
    const requiresSelector = ['click', 'type', 'select', 'download'].includes(draft.action);
    const requiresValue = ['navigate', 'type', 'select'].includes(draft.action);
    const set = (patch) => onChange({ ...draft, ...patch });
    const setSelector = (key, value) => onChange({
        ...draft, selector: { ...(draft.selector || {}), [key]: value },
    });

    return (
        <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
            <DialogTitle>{isNew ? 'Add Step' : 'Edit Step'}</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    <FormControl size="small" fullWidth>
                        <InputLabel>Action</InputLabel>
                        <Select label="Action" value={draft.action}
                                onChange={(e) => set({ action: e.target.value })}>
                            {ACTIONS.map((a) => (
                                <MenuItem key={a.value} value={a.value}>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        {a.icon}<span>{a.label}</span>
                                    </Stack>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {requiresSelector && (
                        <>
                            <TextField
                                size="small"
                                label="Primary selector (CSS or Playwright role=…)"
                                value={draft.selector?.primary || ''}
                                onChange={(e) => setSelector('primary', e.target.value)}
                                placeholder="e.g. button[name='submit'] or role=button[name='Login']"
                                sx={{ fontFamily: 'monospace' }}
                            />
                            <TextField
                                size="small"
                                label="XPath fallback (optional)"
                                value={draft.selector?.xpath || ''}
                                onChange={(e) => setSelector('xpath', e.target.value)}
                                placeholder="//button[contains(., 'Login')]"
                                sx={{ fontFamily: 'monospace' }}
                            />
                            <TextField
                                size="small"
                                label="Visible text fallback (optional)"
                                value={draft.selector?.text || ''}
                                onChange={(e) => setSelector('text', e.target.value)}
                                placeholder="Login"
                            />
                        </>
                    )}

                    {requiresValue && (
                        <TextField
                            size="small"
                            label={
                                draft.action === 'navigate' ? 'URL' :
                                draft.action === 'select' ? 'Option value' : 'Value'
                            }
                            value={draft.value || ''}
                            onChange={(e) => set({ value: e.target.value })}
                            placeholder={
                                draft.action === 'navigate' ? 'https://...' :
                                draft.action === 'type' ? '{{__username__}} or {{period_start}}' : ''
                            }
                            multiline={draft.action !== 'navigate'}
                        />
                    )}

                    {draft.action === 'wait' && (
                        <TextField
                            size="small"
                            label="Wait duration (ms) OR a selector to wait for"
                            value={draft.value || ''}
                            onChange={(e) => set({ value: e.target.value })}
                            placeholder="2000  OR  #invoice-table"
                        />
                    )}

                    {/* Per-step relative date filter — only meaningful for
                        actions that type or select a value into a date input. */}
                    {(draft.action === 'type' || draft.action === 'select') && (
                        <StepDateFilter
                            value={draft.date_filter}
                            recordedLiteral={draft.value}
                            onChange={(next) => set({ date_filter: next })}
                        />
                    )}

                    <Divider />
                    <TextField
                        size="small"
                        label="Semantic label (used by AI self-heal if selector breaks)"
                        value={draft.semantic_label || ''}
                        onChange={(e) => set({ semantic_label: e.target.value })}
                        placeholder='e.g. "Download Statement button" or "Period start date input"'
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onCancel}>Cancel</Button>
                <Button variant="contained" onClick={onCommit}>
                    {isNew ? 'Add' : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}


// ---------------------------------------------------------------------------
// Browser recorder — real session: spawns a Playwright browser on the
// worker host, captures every click/type, streams steps live into the UI.
// ---------------------------------------------------------------------------

function BrowserRecorder({ merchantId, workflowName, paramsSchemaText,
                          onSavedToDashboardTab }) {
    // Mobile vs desktop split. The recording experience is fundamentally
    // different on the two:
    //
    //   - Desktop (md+): we launch a HEADFUL Chromium window on the host
    //     running klonit-api. The user drives that real window directly
    //     with their physical mouse + keyboard. The dashboard shows a
    //     read-only mirror via screenshot streaming.
    //
    //   - Mobile (sm-): there's no separate window to drive (mobile users
    //     can't move focus to a Chromium process on the API host). We
    //     launch HEADLESS Chromium on the host, stream screenshots into
    //     a "screen-wise" canvas inside the dashboard, and forward every
    //     tap/keystroke from the dashboard to the headless browser via
    //     the recorder /input endpoint. The user taps "Stop & save"
    //     (instead of closing a window) to end the recording.
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const [phase, setPhase] = useState('idle');
    // idle → starting → running → stopping → finished | failed
    const [sessionId, setSessionId] = useState(null);
    const [sessionStatus, setSessionStatus] = useState(null);
    // Live download notifications — snackbars surfaced as the recording
    // captures files. The status poll feeds `recent_downloads` and we
    // diff against the IDs we've already toasted so each event fires
    // exactly once.
    const [downloadToasts, setDownloadToasts] = useState([]);
    const seenDownloadIdsRef = useRef(new Set());
    const [steps, setSteps] = useState([]);
    const [error, setError] = useState(null);

    // Post-recording step editor — once the user stops the recording they
    // can click any captured step to open the same StepEditor used by the
    // manual builder. This is where date filter recipes (yesterday, start
    // of last quarter, etc.) get attached. We update the local `steps`
    // state directly; the polling loop early-returns once phase != running
    // so edits aren't clobbered.
    const [editStepIdx, setEditStepIdx] = useState(null); // index into steps[]
    const [editStepDraft, setEditStepDraft] = useState(null);
    // Step the user clicked Delete on, awaiting confirmation.
    const [pendingDeleteIdx, setPendingDeleteIdx] = useState(null);

    const openEditStep = (idx) => {
        const s = steps[idx];
        if (!s) return;
        // Reuse normaliseSeedStep so the StepEditor's expected shape is
        // produced (selector flattened to primary/xpath/text, date_filter
        // carried through).
        setEditStepDraft(normaliseSeedStep(s, idx));
        setEditStepIdx(idx);
    };

    const commitEditStep = () => {
        if (editStepIdx == null || !editStepDraft) return;
        setSteps((prev) => {
            const next = [...prev];
            const original = next[editStepIdx];
            if (!original) return prev;
            // Merge: preserve server-side fields (id, step_idx, captured_at)
            // and overlay the edited fields (action, selector, value,
            // semantic_label, date_filter).
            next[editStepIdx] = {
                ...original,
                action: editStepDraft.action,
                selector: editStepDraft.selector,
                value: editStepDraft.value,
                semantic_label: editStepDraft.semantic_label,
                date_filter: cleanStepDateFilter(editStepDraft.date_filter),
            };
            return next;
        });
        setEditStepIdx(null);
        setEditStepDraft(null);
    };

    const confirmDeleteStep = () => {
        if (pendingDeleteIdx == null) return;
        setSteps((prev) => {
            const victim = prev[pendingDeleteIdx];
            // Track the DB id so the polling tick doesn't resurrect
            // this step on its next fetch from the backend (the
            // RPARunStep row still exists in the DB during recording;
            // we filter it out client-side until the workflow is
            // saved, at which point the saved steps_json doesn't
            // include it either).
            if (victim && victim.id != null) {
                deletedStepIdsRef.current.add(victim.id);
            }
            const next = prev.filter((_, i) => i !== pendingDeleteIdx);
            // Re-index step_idx contiguously so the next round of
            // edits / save lines up. The server side does the same
            // on its own re-normalise pass, but keeping local state
            // consistent prevents the UI from showing gaps.
            return next.map((s, i) => ({ ...s, step_idx: i }));
        });
        setPendingDeleteIdx(null);
    };

    const cancelEditStep = () => {
        setEditStepIdx(null);
        setEditStepDraft(null);
    };

    // Frame polling for the embedded browser canvas. We fetch each
    // screenshot via axios (so JWT + team-id headers travel), decode it
    // off the main thread with createImageBitmap, and blit it onto a
    // <canvas>. Going through canvas instead of <img src=blobUrl> swaps
    // eliminates the per-frame flicker (no DOM element re-evaluating its
    // src, no momentary blank between frames) — the canvas just gets a
    // new pixel layer painted onto the same surface.
    const [frameError, setFrameError] = useState(null);
    const [hasFrame, setHasFrame] = useState(false);
    const [urlBar, setUrlBar] = useState('');
    const [busyInput, setBusyInput] = useState(false);
    const canvasRef = React.useRef(null);
    // Mobile-only typing helper: the headless Chromium has no real
    // keyboard, so we accumulate the user's keystrokes in this buffer
    // and ship them to the focused field on Enter.
    const [typeBuffer, setTypeBuffer] = useState('');

    // RPARunStep.id values the user has chosen to delete locally
    // during a live recording. Lives in a ref so updating it doesn't
    // trigger renders — the polling tick reads it on each fetch and
    // filters the server's step list before calling setSteps, so a
    // step the user deleted can't "come back" 1.5 s later when the
    // server (which still has the row in the DB) sends its next
    // snapshot. The set is cleared whenever a new recording starts.
    const deletedStepIdsRef = React.useRef(new Set());

    const VIEWPORT_W = sessionStatus?.viewport?.width || 1280;
    const VIEWPORT_H = sessionStatus?.viewport?.height || 800;

    // Poll steps + status while a session is live.
    useEffect(() => {
        if (!sessionId || (phase !== 'running' && phase !== 'stopping')) return;
        let cancelled = false;
        // Tracks consecutive polls where the response had no
        // session_status. The backend removes the session from its in-
        // memory registry shortly after the worker exits; if our poll
        // lands right after that pop, session_status is missing and
        // we'd otherwise stay stuck in 'running' forever (lockdown
        // never lifts). After ~3 missing polls we treat the recording
        // as ended and unlock.
        let consecutiveMissing = 0;
        const tick = async () => {
            try {
                const r = await rpaRecordingSteps(sessionId);
                if (cancelled) return;
                if (r?.data?.success) {
                    // Filter out any steps the user just deleted locally —
                    // they still exist in the backend DB so a naive
                    // setSteps would resurrect them on every poll.
                    const incoming = r.data.data || [];
                    const deleted = deletedStepIdsRef.current;
                    const filtered = deleted.size === 0
                        ? incoming
                        : incoming.filter((s) => !deleted.has(s.id));
                    setSteps(filtered);
                    if (r.data.session_status) {
                        consecutiveMissing = 0;
                        setSessionStatus(r.data.session_status);
                        // Sync the URL bar with the actual page URL —
                        // user can override by typing in the bar.
                        if (r.data.session_status.current_url
                            && !document.activeElement?.classList?.contains('rpa-url-input')) {
                            setUrlBar(r.data.session_status.current_url);
                        }
                        const s = r.data.session_status.status;
                        if (s === 'finished' || s === 'failed') {
                            setPhase(s);
                        }
                    } else {
                        consecutiveMissing += 1;
                        if (consecutiveMissing >= 3) {
                            // Worker has exited and been removed from
                            // the registry — recording is over.
                            setPhase('finished');
                        }
                    }
                }
            } catch { /* keep polling */ }
        };
        tick();
        const t = setInterval(tick, 1500);
        return () => { cancelled = true; clearInterval(t); };
    }, [sessionId, phase]);

    // Diff status.recent_downloads against the IDs we've toasted before;
    // fire a snackbar for each new entry. Three states surface:
    //   started → blue snackbar "Downloading invoice.pdf…"
    //   saved   → green snackbar "Saved invoice.pdf (123 KB)"
    //   failed  → red snackbar "Failed to capture invoice.pdf — <reason>"
    useEffect(() => {
        const recent = sessionStatus?.recent_downloads || [];
        if (recent.length === 0) return;
        const seen = seenDownloadIdsRef.current;
        const fresh = recent.filter((e) => !seen.has(e.id));
        if (fresh.length === 0) return;
        for (const e of fresh) seen.add(e.id);
        const toasts = fresh.map((e) => ({
            id: e.id,
            severity: e.state === 'saved' ? 'success'
                    : e.state === 'failed' ? 'error'
                    : 'info',
            message: e.state === 'saved'
                ? `Saved ${e.filename}${e.bytes ? ` (${formatBytesShort(e.bytes)})` : ''}`
                : e.state === 'failed'
                ? `Failed to capture ${e.filename}${e.error ? ` — ${e.error}` : ''}`
                : `Downloading ${e.filename}…`,
            autoHide: e.state === 'started' ? null : 4000,
        }));
        setDownloadToasts((cur) => [...cur, ...toasts].slice(-3));
    }, [sessionStatus?.recent_downloads]);

    // Frame polling — only runs on mobile. On desktop the user drives
    // the real Chromium window directly and the dashboard shows the
    // captured-step list in the center instead of a screenshot mirror,
    // so we don't pay the per-second network cost or burn CPU on
    // decoding frames nobody renders.
    //
    // On mobile the canvas IS the browser surface — taps + scrolls go
    // through it, so we need fresh frames at ~2 fps. We fetch each JPEG
    // via axios (auth headers attached), decode off the main thread
    // with createImageBitmap, then blit the bitmap onto a <canvas>.
    // The canvas approach replaces a prior <img src={blobUrl}> swap that
    // caused per-frame flicker (each src change made the <img>
    // momentarily re-evaluate / tear, and the surrounding aspect-ratio
    // container appeared to "zoom in / zoom out" as the URL bar text
    // wrapped to different numbers of lines on every navigation).
    useEffect(() => {
        if (phase !== 'running' || !sessionId) return;
        if (!isMobile) return;
        let cancelled = false;
        const fetchFrame = async () => {
            try {
                const r = await axios.get(rpaRecordingFrameUrl(sessionId), {
                    responseType: 'arraybuffer',
                    validateStatus: (s) => s === 200 || s === 204,
                });
                if (cancelled) return;
                if (r.status === 204 || !r.data || r.data.byteLength === 0) {
                    return;
                }
                const ctype = r.headers?.['content-type'] || 'image/jpeg';
                const blob = new Blob([r.data], { type: ctype });

                // Off-main-thread decode where supported (all modern
                // browsers). Falls back to <Image> + URL.createObjectURL
                // on the rare browser without createImageBitmap.
                let bitmap;
                let fallbackUrl = null;
                if (typeof createImageBitmap === 'function') {
                    bitmap = await createImageBitmap(blob);
                } else {
                    fallbackUrl = URL.createObjectURL(blob);
                    const img = new window.Image();
                    await new Promise((res, rej) => {
                        img.onload = res;
                        img.onerror = rej;
                        img.src = fallbackUrl;
                    });
                    bitmap = img;
                }
                if (cancelled) {
                    if (bitmap && typeof bitmap.close === 'function') bitmap.close();
                    if (fallbackUrl) URL.revokeObjectURL(fallbackUrl);
                    return;
                }
                const canvas = canvasRef.current;
                if (canvas) {
                    if (canvas.width !== bitmap.width) canvas.width = bitmap.width;
                    if (canvas.height !== bitmap.height) canvas.height = bitmap.height;
                    const ctx = canvas.getContext('2d');
                    if (ctx) ctx.drawImage(bitmap, 0, 0);
                }
                if (bitmap && typeof bitmap.close === 'function') bitmap.close();
                if (fallbackUrl) URL.revokeObjectURL(fallbackUrl);
                setHasFrame(true);
                setFrameError(null);
            } catch (e) {
                if (!cancelled) {
                    const status = e?.response?.status;
                    setFrameError(
                        status === 401 || status === 403
                            ? 'Auth failed — re-login may be required.'
                            : status === 404
                            ? 'Recording session not found.'
                            : `Frame fetch failed (${status || e.message})`
                    );
                }
            }
        };
        fetchFrame();
        const intervalMs = isMobile ? 500 : 1000;
        const t = setInterval(fetchFrame, intervalMs);
        return () => {
            cancelled = true;
            clearInterval(t);
        };
    }, [phase, sessionId, isMobile]);

    const start = async () => {
        if (!merchantId) { setError('Pick a merchant first.'); return; }
        setError(null);
        setHasFrame(false);
        setFrameError(null);
        // Reset the deletion tracker — this is a fresh recording, no
        // leftover deletions from a previous session.
        deletedStepIdsRef.current = new Set();
        setPhase('starting');
        try {
            const r = await rpaRecordingStart({
                merchant_id: merchantId,
                workflow_name: workflowName || 'Recorded workflow',
                // Tell the backend whether to launch the Chromium window
                // headful (desktop — user drives a real window) or
                // headless (mobile — only the dashboard can interact).
                is_mobile: isMobile,
            });
            if (!r?.data?.success) throw new Error(r?.data?.message || 'Start failed');
            const sid = r.data.data.session_id;
            const sess = r.data.data.recording_session;
            setSessionId(sid);
            setSessionStatus(sess);
            if (sess?.status === 'failed') {
                setPhase('failed');
                setError(sess.error || 'Could not start the browser.');
            } else {
                setPhase('running');
            }
        } catch (e) {
            setPhase('failed');
            setError(e?.response?.data?.message || e.message);
        }
    };

    // ---- input forwarding ------------------------------------------

    // Translate dashboard-canvas pixel coords to viewport coords. The
    // <canvas> element scales to its container; we read displayed
    // bounding rect dims and scale x,y back to the real viewport.
    const sendClick = React.useCallback(async (clientX, clientY) => {
        if (!sessionId || !canvasRef.current) return;
        const rect = canvasRef.current.getBoundingClientRect();
        if (rect.width <= 0 || rect.height <= 0) return;
        const x = ((clientX - rect.left) / rect.width) * VIEWPORT_W;
        const y = ((clientY - rect.top) / rect.height) * VIEWPORT_H;
        try {
            setBusyInput(true);
            await rpaRecordingInput(sessionId, {
                action: 'click', x, y,
            });
        } catch (e) {
            setError(e?.response?.data?.message || e.message);
        } finally {
            setBusyInput(false);
        }
    }, [sessionId, VIEWPORT_W, VIEWPORT_H]);

    const sendKey = React.useCallback(async (key) => {
        if (!sessionId) return;
        try {
            await rpaRecordingInput(sessionId, { action: 'key', key });
        } catch (e) {
            setError(e?.response?.data?.message || e.message);
        }
    }, [sessionId]);

    const sendType = React.useCallback(async (text) => {
        if (!sessionId || !text) return;
        try {
            await rpaRecordingInput(sessionId, { action: 'type', text });
        } catch (e) {
            setError(e?.response?.data?.message || e.message);
        }
    }, [sessionId]);

    const sendNavigate = React.useCallback(async (url) => {
        if (!sessionId || !url) return;
        try {
            await rpaRecordingInput(sessionId, { action: 'navigate', url });
        } catch (e) {
            setError(e?.response?.data?.message || e.message);
        }
    }, [sessionId]);

    const sendScroll = React.useCallback(async (dx, dy) => {
        if (!sessionId) return;
        try {
            await rpaRecordingInput(sessionId, { action: 'scroll', dx, dy });
        } catch { /* swallow — scroll is best-effort */ }
    }, [sessionId]);

    // ---- canvas event handlers ------------------------------------

    const onCanvasClick = (e) => {
        e.preventDefault();
        sendClick(e.clientX, e.clientY);
    };

    const onCanvasKeyDown = (e) => {
        // Single-character printable → type. Special keys → forward by name.
        const k = e.key;
        if (k.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
            e.preventDefault();
            sendType(k);
            return;
        }
        // Common navigation / form keys we want to support.
        const SPECIAL = new Set([
            'Enter', 'Tab', 'Escape', 'Backspace', 'Delete',
            'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
            'Home', 'End', 'PageUp', 'PageDown',
        ]);
        if (SPECIAL.has(k)) {
            e.preventDefault();
            sendKey(k);
        }
    };

    const onCanvasWheel = (e) => {
        // Forward modest scrolls; ignore tiny ones to reduce chatter.
        if (Math.abs(e.deltaY) < 4 && Math.abs(e.deltaX) < 4) return;
        sendScroll(e.deltaX, e.deltaY);
    };

    const onUrlGo = () => {
        if (!urlBar.trim()) return;
        sendNavigate(urlBar.trim());
    };

    const stop = async () => {
        if (!sessionId) return;
        setPhase('stopping');
        try {
            await rpaRecordingStop(sessionId);
        } catch (e) {
            setError(e?.response?.data?.message || e.message);
        }
    };

    const saveAsWorkflow = async () => {
        if (!sessionId) return;
        if (!workflowName?.trim()) {
            setError('Enter a workflow name in the header before saving.');
            return;
        }
        let paramsSchema = null;
        if (paramsSchemaText && paramsSchemaText.trim()) {
            try { paramsSchema = JSON.parse(paramsSchemaText); }
            catch { setError('Params schema must be valid JSON or empty.'); return; }
        }
        setError(null);

        // Snapshot the LOCAL steps (which include any edits the user
        // made in the post-recording StepEditor — renamed actions,
        // edited values, attached date-filter recipes) and ship them
        // to the backend. Without this, the backend would only see
        // the raw captured rows from the DB and the user's edits
        // would be silently dropped from the saved workflow.
        const stepsForSaveOut = (steps || []).map((s, idx) => {
            const sel = {};
            if (s.selector?.primary) sel.primary = s.selector.primary;
            if (s.selector?.xpath) sel.xpath = s.selector.xpath;
            if (s.selector?.text) sel.text = s.selector.text;
            const out = {
                step_idx: idx,
                action: s.action,
                selector: sel,
            };
            if (s.value !== undefined && s.value !== null && s.value !== '') {
                out.value = s.value;
            }
            if (s.semantic_label) out.semantic_label = s.semantic_label;
            const df = cleanStepDateFilter(s.date_filter);
            if (df) out.date_filter = df;
            return out;
        });

        try {
            const r = await rpaRecordingFinalize(sessionId, {
                workflow_name: workflowName.trim(),
                params_schema: paramsSchema,
                steps: stepsForSaveOut,
            });
            if (!r?.data?.success) throw new Error(r?.data?.message || 'Finalize failed');
            onSavedToDashboardTab?.();
        } catch (e) {
            setError(e?.response?.data?.message || e.message);
        }
    };

    const discardSession = async () => {
        if (!sessionId) return;
        if (!window.confirm('Discard this recording session? Steps will not be saved.')) return;
        try {
            await rpaRecordingDiscard(sessionId);
        } catch { /* fire and forget */ }
        setSessionId(null);
        setSessionStatus(null);
        setSteps([]);
        deletedStepIdsRef.current = new Set();
        setPhase('idle');
        setError(null);
    };

    if (phase === 'idle') {
        return (
            <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
                <OpenInBrowserIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
                <Typography variant="h6" sx={{ mb: 1 }}>
                    Start an in-browser recording
                </Typography>
                <Typography variant="body2" color="text.secondary"
                            sx={{ maxWidth: 600, mx: 'auto', mb: 2 }}>
                    {isMobile ? (
                        <>
                            Click <strong>Start</strong> — a Chromium browser
                            spins up server-side and renders right here in
                            this screen. Tap on the page to interact (login,
                            navigate, pick dates, tap Download). Every action
                            is captured as a workflow step. When you're
                            finished, tap <strong>Stop recording</strong> —
                            the captured steps appear and you can attach
                            date-filter recipes (yesterday, start of last
                            quarter, etc.) before saving the workflow.
                        </>
                    ) : (
                        <>
                            Click <strong>Start</strong> — a real Chromium
                            window opens on this machine. Run through your
                            merchant workflow there (login, navigate, pick
                            dates, click Download). Every click, form entry,
                            and download is captured as a workflow step. When
                            you're finished, <strong>just close the Chromium
                            window</strong> — the editor on this dashboard
                            will unlock so you can attach date-filter recipes
                            (yesterday, start of last quarter, etc.) before
                            saving the workflow.
                        </>
                    )}
                </Typography>
                {!merchantId && (
                    <Alert severity="warning" sx={{ maxWidth: 600, mx: 'auto', mb: 2 }}>
                        Pick a merchant in the header above before starting.
                    </Alert>
                )}
                {error && <Alert severity="error" sx={{ maxWidth: 600, mx: 'auto', mb: 2 }}>{error}</Alert>}
                <Button
                    variant="contained" size="large"
                    startIcon={<OpenInBrowserIcon />}
                    onClick={start} disabled={!merchantId}
                >
                    Start recording
                </Button>
                <Divider sx={{ my: 3 }} />
                <Typography variant="caption" color="text.secondary">
                    {isMobile
                        ? 'Mobile: browser runs headlessly on klonit-api and streams here. Taps + scrolls + the typing box below send to the page.'
                        : 'Desktop: browser opens on the machine running klonit-api. Production deploys can stream it via noVNC (operator setup required).'}
                </Typography>
            </Paper>
        );
    }

    const fullscreenActive = phase === 'running' || phase === 'stopping';

    // Desktop, while a recording is in flight, drops the live preview
    // entirely — the user is driving the real Chromium window so a
    // mirror in the dashboard adds no value (and the streaming + 1-fps
    // canvas refresh was reading as flicker / zoom). Instead the
    // captured-step list takes the center, so the user can watch steps
    // accumulate as they click around in Chromium. Mobile is unchanged:
    // the canvas is the only browser surface there.
    const desktopRecording = !isMobile
        && (phase === 'running' || phase === 'stopping');

    // Post-recording layout swap. Once the Chromium window is closed
    // (phase: finished / failed) the captured steps become the *focus*
    // of the screen — they're what the user is about to edit and save.
    // Promote the step list into the wide 1fr column and demote the
    // status / actions panel into the narrow 360px right column. CSS
    // `order` on the two grid items handles the swap without
    // restructuring the JSX or duplicating the (large) status panel.
    const postRecording = phase === 'finished' || phase === 'failed';

    // Captured-step list. Rendered in two possible spots — extracted
    // here so both positions render exactly the same content:
    //   - Right column (default 2-col layout) when not desktop-recording
    //   - Center of the left panel (replacing the canvas) when desktop
    //     is recording.
    const stepsListEl = (
        <Paper variant="outlined" sx={{ p: 0, maxHeight: 580, overflow: 'hidden',
                                         display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ px: 2, py: 1.25, borderBottom: '1px solid rgba(15,23,42,0.08)' }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        Captured steps
                    </Typography>
                    <Chip size="small" label={steps.length} />
                </Stack>
            </Box>
            <Box sx={{ flex: 1, overflow: 'auto', p: 1, minHeight: 240 }}>
                {steps.length === 0 ? (
                    <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
                        <Typography variant="body2">
                            Steps will appear here as you click around in the browser.
                        </Typography>
                    </Box>
                ) : (
                    <Stack spacing={0.75}>
                        {steps.map((s, idx) => {
                            const editable = phase === 'finished' || phase === 'stopping';
                            const dfChip = s.date_filter && s.date_filter.preset
                                ? <Chip size="small" label={`📅 ${s.date_filter.preset}`}
                                        color="info" variant="outlined"
                                        sx={{ height: 18, fontSize: 10 }} />
                                : null;
                            return (
                                <Paper
                                    key={s.id}
                                    variant="outlined"
                                    onClick={editable ? () => openEditStep(idx) : undefined}
                                    sx={{
                                        p: 1,
                                        cursor: editable ? 'pointer' : 'default',
                                        transition: 'border-color 120ms, background 120ms',
                                        ...(editable && {
                                            '&:hover': {
                                                borderColor: 'primary.main',
                                                background: 'rgba(99, 102, 241, 0.04)',
                                            },
                                        }),
                                    }}
                                >
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <Chip size="small" label={`#${s.step_idx + 1}`}
                                              sx={{ minWidth: 36 }} />
                                        {ACTION_ICON[s.action]}
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            {ACTION_LABEL[s.action] || s.action}
                                        </Typography>
                                        <Box sx={{ flex: 1 }} />
                                        {dfChip}
                                        {editable && (
                                            <Tooltip title="Edit step (set date filter, etc.)">
                                                <EditIcon sx={{
                                                    fontSize: 14,
                                                    color: 'text.disabled',
                                                }} />
                                            </Tooltip>
                                        )}
                                        {/* Delete is allowed at any phase — including
                                            during recording, so the user can drop a
                                            misclick step without stopping the session. */}
                                        <Tooltip title="Delete this step">
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={(e) => {
                                                    // Don't bubble to the Paper's onClick
                                                    // which opens the edit dialog.
                                                    e.stopPropagation();
                                                    setPendingDeleteIdx(idx);
                                                }}
                                                sx={{ padding: 0.25 }}
                                            >
                                                <DeleteIcon sx={{ fontSize: 14 }} />
                                            </IconButton>
                                        </Tooltip>
                                    </Stack>
                                    {s.semantic_label && (
                                        <Typography variant="caption" sx={{
                                            display: 'block',
                                            color: 'text.secondary',
                                            ml: 5,
                                        }}>
                                            {s.semantic_label}
                                        </Typography>
                                    )}
                                    {(s.selector?.primary || s.selector_used) && (
                                        <Typography variant="caption" sx={{
                                            display: 'block',
                                            fontFamily: 'monospace',
                                            color: 'text.disabled',
                                            ml: 5,
                                            wordBreak: 'break-all',
                                        }}>
                                            {s.selector?.primary || s.selector_used}
                                        </Typography>
                                    )}
                                    {s.value && s.action !== 'navigate' && (
                                        <Typography variant="caption" sx={{
                                            display: 'block',
                                            ml: 5,
                                            color: 'text.secondary',
                                        }}>
                                            value: {redactPwd(String(s.value))}
                                        </Typography>
                                    )}
                                </Paper>
                            );
                        })}
                    </Stack>
                )}
            </Box>
        </Paper>
    );

    return (
      <>
        <Box sx={{
            display: 'grid',
            // Even 50/50 split on md+. The narrower 360-px sidebar
            // worked while the right column was just a live ticker of
            // captured rows, but now that the layout swaps after the
            // browser closes — steps on the left, status / actions on
            // the right — both halves carry useful content and
            // deserve equal screen real estate.
            gridTemplateColumns: desktopRecording
                ? '1fr'
                : { xs: '1fr', md: '1fr 1fr' },
            gap: 2,
            // align-items: start (not the default `stretch`) so the
            // shorter panel doesn't get blown up to match the taller
            // one. Without this, the status panel — which is only a
            // chip + alert + two buttons tall after recording ends —
            // gets stretched to the steps-list height and reads as a
            // big empty footer in the right column.
            alignItems: 'start',
        }}>
            {/* Status panel.
                Default (idle / recording): LEFT 1fr column.
                Post-recording (finished/failed): RIGHT 360px column —
                the steps list takes the focus so this panel is the
                compact "what next?" sidebar. The grid auto-places
                items by source order; CSS `order` overrides that to
                swap the two slots without restructuring the JSX.
                No fixed minHeight while post-recording: the
                post-recording content (chip + finished alert +
                Save/Discard) is short, so a 360-px floor was
                producing a tall blank strip below the buttons that
                read as a dead footer. Letting the Paper size to its
                content removes that gap. The 360-px floor is kept
                during starting / running / stopping so the spinner
                state doesn't visually collapse. */}
            <Paper variant="outlined" sx={{
                p: 3,
                minHeight: postRecording ? 0 : 360,
                order: postRecording ? 1 : 0,
            }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                    <Chip
                        size="small" color={phaseColor(phase)}
                        label={phaseLabel(phase)}
                        sx={{ fontWeight: 700 }} />
                    {sessionStatus?.mode && (
                        <Chip size="small" variant="outlined"
                              label={sessionStatus.mode === 'headful_local'
                                  ? 'Browser opens on your machine'
                                  : sessionStatus.mode === 'vnc_remote'
                                  ? 'Streaming via VNC'
                                  : sessionStatus.mode} />
                    )}
                    <Box sx={{ flex: 1 }} />
                    <Typography variant="caption" color="text.secondary">
                        Session #{sessionId}
                    </Typography>
                </Stack>

                {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

                {phase === 'starting' && (
                    <Stack alignItems="center" sx={{ py: 4 }}>
                        <CircularProgress size={28} sx={{ mb: 2 }} />
                        <Typography variant="body2" color="text.secondary">
                            Opening Chromium browser…
                        </Typography>
                    </Stack>
                )}

                {(phase === 'running' || phase === 'stopping') && (
                    <Stack
                        spacing={1.5}
                        // No blanket pointer-events:none here. The canvas
                        // (mobile only) sets its own pointer-events. The
                        // captured-step list (desktop) needs to be
                        // scrollable, just not editable until the
                        // recording ends — that's enforced inside the
                        // step rows themselves via the `editable` flag.
                    >
                        {/* Lock-down banner — phrasing depends on which
                            browser surface the user is driving. */}
                        <Alert severity="warning" icon={
                            <Box sx={{
                                width: 10, height: 10,
                                borderRadius: '50%',
                                background: '#dc2626',
                                animation: 'rpa-rec-dot-status 1.4s ease-in-out infinite',
                                mt: '6px',
                                '@keyframes rpa-rec-dot-status': {
                                    '0%, 100%': { opacity: 1, transform: 'scale(1)' },
                                    '50%':       { opacity: 0.5, transform: 'scale(0.7)' },
                                },
                            }} />
                        } sx={{ alignItems: 'center' }}>
                            {isMobile ? (
                                <>
                                    <strong>Recording — tap on the page below to interact.</strong>{' '}
                                    Tap <strong>Stop recording</strong> when you're done — the
                                    captured steps will appear so you can edit them before saving.
                                </>
                            ) : (
                                <>
                                    <strong>Recording — close the Chromium window when you're done.</strong>{' '}
                                    The dashboard is locked until you do.
                                </>
                            )}
                            {sessionStatus?.current_url ? (
                                // Single-line + ellipsis. wordBreak:break-all
                                // (the previous setting) made the URL wrap
                                // to multiple lines on every navigation,
                                // which changed the alert height — and that
                                // resized the aspect-ratio canvas underneath
                                // it on every page transition. Result: the
                                // preview "zoomed" in and out as the user
                                // navigated. Keeping the URL on one line
                                // freezes the layout.
                                <Box component="span" sx={{
                                    display: 'block',
                                    mt: 0.5,
                                    fontFamily: 'monospace',
                                    fontSize: 11,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    color: 'text.secondary',
                                }}
                                title={sessionStatus.current_url}
                                >
                                    {sessionStatus.current_url}
                                </Box>
                            ) : null}
                        </Alert>

                        {/* Mobile only: interactive canvas — taps are
                            forwarded to the headless Chromium running on
                            klonit-api. Desktop: the canvas is suppressed
                            and the captured-step list (rendered below)
                            takes the center so the user has something
                            useful to watch while they drive the real
                            Chromium window. */}
                        {isMobile ? (
                        <Box
                            tabIndex={isMobile ? 0 : -1}
                            onKeyDown={isMobile ? onCanvasKeyDown : undefined}
                            onWheel={isMobile ? onCanvasWheel : undefined}
                            sx={{
                                position: 'relative',
                                background: '#0f172a',
                                borderRadius: 1,
                                overflow: 'hidden',
                                aspectRatio: `${VIEWPORT_W} / ${VIEWPORT_H}`,
                                // Static red border. The previous pulsing
                                // animation (border-color + box-shadow
                                // alternating every 2.5s) read as flicker
                                // / zoom in peripheral vision when the
                                // canvas was simultaneously updating with
                                // each new frame. The pulsing red dot in
                                // the alert + REC tag still signals
                                // "recording in progress" without making
                                // the whole preview throb.
                                border: '3px solid #dc2626',
                                outline: 'none',
                                cursor: isMobile
                                    ? (busyInput ? 'wait' : 'crosshair')
                                    : 'default',
                                '&:focus-visible': { outline: 'none' },
                            }}
                        >
                            {/* Persistent canvas — frames are blitted on
                                top of the existing pixels, so there's no
                                tear / flash between updates. */}
                            <canvas
                                ref={canvasRef}
                                width={VIEWPORT_W}
                                height={VIEWPORT_H}
                                onClick={isMobile ? onCanvasClick : undefined}
                                style={{
                                    display: 'block',
                                    width: '100%',
                                    height: '100%',
                                    background: '#fff',
                                    userSelect: 'none',
                                    // Mobile: clickable when not in flight.
                                    // Desktop: always pointer-events:none —
                                    // user drives the real Chromium window.
                                    pointerEvents: (isMobile && !busyInput && hasFrame)
                                        ? 'auto'
                                        : 'none',
                                }}
                            />
                            {!hasFrame && (
                                <Stack alignItems="center" justifyContent="center"
                                       spacing={1.5}
                                       sx={{
                                           position: 'absolute',
                                           inset: 0,
                                           background: '#0f172a',
                                           pointerEvents: 'none',
                                       }}>
                                    <CircularProgress sx={{ color: '#94a3b8' }} />
                                    <Typography variant="body2"
                                                sx={{ color: '#94a3b8' }}>
                                        Waiting for first frame…
                                    </Typography>
                                </Stack>
                            )}

                            {/* ● REC tag overlay */}
                            <Box sx={{
                                position: 'absolute',
                                top: 8, left: 8,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.75,
                                background: 'rgba(15,23,42,0.85)',
                                color: '#fff',
                                fontSize: 11,
                                fontWeight: 800,
                                letterSpacing: 0.6,
                                px: 1, py: 0.4,
                                borderRadius: 0.75,
                                pointerEvents: 'none',
                            }}>
                                <Box sx={{
                                    width: 8, height: 8,
                                    borderRadius: '50%',
                                    background: '#dc2626',
                                    animation: 'rpa-rec-dot-status 1.4s ease-in-out infinite',
                                }} />
                                REC
                            </Box>

                            {busyInput && isMobile && (
                                <Box sx={{
                                    position: 'absolute',
                                    top: 8, right: 8,
                                    background: 'rgba(15,23,42,0.85)',
                                    color: '#fff',
                                    fontSize: 11, px: 1, py: 0.25,
                                    borderRadius: 1,
                                    pointerEvents: 'none',
                                }}>
                                    sending…
                                </Box>
                            )}

                            {frameError && (
                                <Box sx={{
                                    position: 'absolute',
                                    bottom: 8, left: 8, right: 8,
                                    background: 'rgba(245,158,11,0.95)',
                                    color: '#fff',
                                    fontSize: 11, fontWeight: 600,
                                    px: 1, py: 0.5, borderRadius: 1,
                                    pointerEvents: 'none',
                                }}>
                                    {frameError}
                                </Box>
                            )}
                        </Box>
                        ) : (
                            // Desktop running: captured-step list takes
                            // the center spot the canvas occupied.
                            stepsListEl
                        )}

                        {/* Mobile: lightweight typing helper. The headless
                            Chromium has no real keyboard — anything the
                            user wants to type into a focused field has to
                            be forwarded explicitly. Tapping into a field
                            on the canvas focuses it on the server side;
                            the user types here, then taps Enter (or the
                            Send button) to ship the buffer through. */}
                        {isMobile && (
                            <Stack direction="row" spacing={1}>
                                <TextField
                                    size="small"
                                    value={typeBuffer}
                                    onChange={(e) => setTypeBuffer(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            if (typeBuffer) {
                                                sendType(typeBuffer);
                                                setTypeBuffer('');
                                            }
                                            sendKey('Enter');
                                        }
                                    }}
                                    placeholder="Type into focused field, then Enter"
                                    inputProps={{ autoCapitalize: 'off',
                                                  autoCorrect: 'off',
                                                  spellCheck: false }}
                                    fullWidth
                                />
                                <Button
                                    variant="outlined" size="small"
                                    onClick={() => {
                                        if (typeBuffer) {
                                            sendType(typeBuffer);
                                            setTypeBuffer('');
                                        }
                                    }}
                                    disabled={!typeBuffer}
                                >
                                    Send
                                </Button>
                            </Stack>
                        )}

                        <Typography variant="caption" color="text.secondary"
                                    sx={{ textAlign: 'center' }}>
                            Captured: <strong>{steps.length}</strong> step(s)
                            {sessionStatus?.step_count != null
                                && sessionStatus.step_count !== steps.length && (
                                <span style={{ color: '#dc2626' }}>
                                    {' '}· worker counted{' '}
                                    <strong>{sessionStatus.step_count}</strong>
                                    {' '}— DB / API mismatch
                                </span>
                            )}
                            {isMobile
                                ? ' · tap on the preview to interact'
                                : ' · steps appear here as you click in the Chromium window'}
                        </Typography>
                    </Stack>
                )}

                {(phase === 'finished' || phase === 'failed') && (
                    <Alert severity={phase === 'finished' ? 'success' : 'error'} sx={{ mt: 2 }}>
                        {phase === 'finished'
                            ? `Recording ended. Captured ${steps.length} step(s). Click any step on the left to edit it — set a relative date filter (yesterday, start of last quarter, etc.) on date inputs before saving so the agent learns dynamic values.`
                            : `Session ended with: ${sessionStatus?.error || error || 'unknown error'}`}
                    </Alert>
                )}

                <Divider sx={{ my: 2 }} />
                {/* Bottom action bar. While the recording is live every
                    button except the path-appropriate Stop is disabled.
                    On desktop, the Stop button is decorative — the user
                    closes the Chromium window to end recording. On mobile,
                    the Stop button IS the end path because there's no
                    separate window to close. */}
                <Stack direction="row" spacing={1} flexWrap="wrap">
                    {(phase === 'running' || phase === 'stopping') && (
                        <Button
                            color="warning"
                            onClick={stop}
                            disabled={phase === 'stopping' || !isMobile}
                        >
                            {phase === 'stopping'
                                ? 'Stopping…'
                                : isMobile
                                    ? 'Stop recording'
                                    : 'Close Chromium to stop'}
                        </Button>
                    )}
                    <Button
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={saveAsWorkflow}
                        disabled={steps.length === 0
                                  || phase === 'starting'
                                  || phase === 'running'
                                  || phase === 'stopping'}
                        sx={(phase === 'running' || phase === 'stopping') ? {
                            opacity: 0.45,
                        } : {}}
                    >
                        Save as workflow
                    </Button>
                    <Button color="error" onClick={discardSession}
                            disabled={phase === 'starting'
                                      || phase === 'running'
                                      || phase === 'stopping'}
                            sx={(phase === 'running' || phase === 'stopping') ? {
                                opacity: 0.45,
                            } : {}}>
                        Discard
                    </Button>
                </Stack>
            </Paper>

            {/* Captured-step list.
                Default (idle): RIGHT 360px column — narrow sidebar that
                fills with rows as recording progresses.
                Post-recording (finished / failed): LEFT 1fr column —
                promoted to the focal point so each row has more room
                for selectors / values / date-filter chips. The grid
                cells are swapped via CSS `order` on this Paper and on
                the status Paper above.
                Hidden during desktop recording because the same list
                is rendered in the center of the status panel
                (replacing the live preview canvas). */}
            {!desktopRecording && (
            <Paper variant="outlined" sx={{ p: 1, maxHeight: 800, overflow: 'hidden',
                                             display: 'flex', flexDirection: 'column',
                                             order: postRecording ? -1 : 0 }}>
                <Box sx={{ px: 2, py: 1.25, borderBottom: '1px solid rgba(15,23,42,0.08)' }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                            Captured steps
                        </Typography>
                        <Chip size="small" label={steps.length} />
                    </Stack>
                </Box>
                <Box sx={{ flex: 1, overflow: 'auto', p: 1 }}>
                    {steps.length === 0 ? (
                        <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
                            <Typography variant="body2">
                                Steps will appear here as you click around in the browser.
                            </Typography>
                        </Box>
                    ) : (
                        <Stack spacing={0.75}>
                            {steps.map((s, idx) => {
                                const editable = phase === 'finished' || phase === 'stopping';
                                const dfChip = s.date_filter && s.date_filter.preset
                                    ? <Chip size="small" label={`📅 ${s.date_filter.preset}`}
                                            color="info" variant="outlined"
                                            sx={{ height: 18, fontSize: 10 }} />
                                    : null;
                                return (
                                    <Paper
                                        key={s.id}
                                        variant="outlined"
                                        onClick={editable ? () => openEditStep(idx) : undefined}
                                        sx={{
                                            p: 1,
                                            cursor: editable ? 'pointer' : 'default',
                                            transition: 'border-color 120ms, background 120ms',
                                            ...(editable && {
                                                '&:hover': {
                                                    borderColor: 'primary.main',
                                                    background: 'rgba(99, 102, 241, 0.04)',
                                                },
                                            }),
                                        }}
                                    >
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <Chip size="small" label={`#${s.step_idx + 1}`}
                                                  sx={{ minWidth: 36 }} />
                                            {ACTION_ICON[s.action]}
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                {ACTION_LABEL[s.action] || s.action}
                                            </Typography>
                                            <Box sx={{ flex: 1 }} />
                                            {dfChip}
                                            {editable && (
                                                <Tooltip title="Edit step (set date filter, etc.)">
                                                    <EditIcon sx={{
                                                        fontSize: 14,
                                                        color: 'text.disabled',
                                                    }} />
                                                </Tooltip>
                                            )}
                                            <Tooltip title="Delete this step">
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setPendingDeleteIdx(idx);
                                                    }}
                                                    sx={{ padding: 0.25 }}
                                                >
                                                    <DeleteIcon sx={{ fontSize: 14 }} />
                                                </IconButton>
                                            </Tooltip>
                                        </Stack>
                                        {s.semantic_label && (
                                            <Typography variant="caption" sx={{
                                                display: 'block',
                                                color: 'text.secondary',
                                                ml: 5,
                                            }}>
                                                {s.semantic_label}
                                            </Typography>
                                        )}
                                        {(s.selector?.primary || s.selector_used) && (
                                            <Typography variant="caption" sx={{
                                                display: 'block',
                                                fontFamily: 'monospace',
                                                color: 'text.disabled',
                                                ml: 5,
                                                wordBreak: 'break-all',
                                            }}>
                                                {s.selector?.primary || s.selector_used}
                                            </Typography>
                                        )}
                                        {s.value && s.action !== 'navigate' && (
                                            <Typography variant="caption" sx={{
                                                display: 'block',
                                                ml: 5,
                                                color: 'text.secondary',
                                            }}>
                                                value: {redactPwd(String(s.value))}
                                            </Typography>
                                        )}
                                    </Paper>
                                );
                            })}
                        </Stack>
                    )}
                </Box>
            </Paper>
            )}
        </Box>

        {/* The headful Chromium window IS the recording surface — no
            FullscreenRecorder portal needed. The status panel above
            tells the user what to do; the captured-step list on the
            right surfaces steps in real time. */}

        {/* Post-recording step editor — clicking a captured step row opens
            this. Reuses the manual-builder StepEditor so date_filter
            recipes (start_of_last_quarter, yesterday, etc.) can be
            attached to any step the user typed a date into. */}
        <StepEditor
            open={editStepIdx != null}
            draft={editStepDraft || EMPTY_STEP}
            onChange={setEditStepDraft}
            onCommit={commitEditStep}
            onCancel={cancelEditStep}
            isNew={false}
        />

        {/* Delete confirmation — shows the step about to be removed
            and asks the user to confirm. The removal updates only the
            local `steps` state; the change is shipped to the backend
            when the user clicks "Save as workflow" (the finalize body
            includes the edited steps array). */}
        <Dialog
            open={pendingDeleteIdx != null}
            onClose={() => setPendingDeleteIdx(null)}
            maxWidth="xs"
            fullWidth
        >
            <DialogTitle>Delete this step?</DialogTitle>
            <DialogContent>
                <Typography variant="body2" sx={{ mb: 1.5 }}>
                    This removes the step from the recorded sequence. The
                    change is staged locally — click "Save as workflow"
                    to persist it.
                </Typography>
                {pendingDeleteIdx != null && steps[pendingDeleteIdx] && (
                    <Paper variant="outlined" sx={{ p: 1.25 }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <Chip size="small"
                                  label={`#${(steps[pendingDeleteIdx].step_idx ?? pendingDeleteIdx) + 1}`}
                                  sx={{ minWidth: 44, fontWeight: 700 }} />
                            {ACTION_ICON[steps[pendingDeleteIdx].action] || null}
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {ACTION_LABEL[steps[pendingDeleteIdx].action]
                                    || steps[pendingDeleteIdx].action}
                            </Typography>
                        </Stack>
                        {(steps[pendingDeleteIdx].selector?.primary
                          || steps[pendingDeleteIdx].selector_used) && (
                            <Typography variant="caption" sx={{
                                display: 'block',
                                mt: 0.5,
                                fontFamily: 'monospace',
                                color: 'text.secondary',
                                wordBreak: 'break-all',
                            }}>
                                {steps[pendingDeleteIdx].selector?.primary
                                 || steps[pendingDeleteIdx].selector_used}
                            </Typography>
                        )}
                        {steps[pendingDeleteIdx].semantic_label && (
                            <Typography variant="caption" sx={{
                                display: 'block',
                                color: 'text.secondary',
                            }}>
                                {steps[pendingDeleteIdx].semantic_label}
                            </Typography>
                        )}
                    </Paper>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setPendingDeleteIdx(null)}>Cancel</Button>
                <Button
                    onClick={confirmDeleteStep}
                    color="error"
                    variant="contained"
                    startIcon={<DeleteIcon fontSize="small" />}
                >
                    Delete step
                </Button>
            </DialogActions>
        </Dialog>

        {/* Live download progress notifications. Stacked at bottom-right
            so they sit out of the way of the recorder canvas. */}
        {downloadToasts.map((t, idx) => (
            <Snackbar
                key={t.id}
                open={true}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                autoHideDuration={t.autoHide}
                onClose={(_, reason) => {
                    if (reason === 'clickaway') return;
                    setDownloadToasts((cur) => cur.filter((x) => x.id !== t.id));
                }}
                // Stack vertically — each toast nudged up by its index. Use
                // template strings so React doesn't coerce the responsive
                // object to '[object Object]px' (which is what the previous
                // version produced — silently broken CSS).
                // The FullscreenRecorder Portal sits at zIndex 1500, so we
                // push the toasts above it (1700) — otherwise download
                // notifications render *behind* the live recorder and the
                // user never sees them while a download is in flight.
                sx={{
                    zIndex: 1700,
                    bottom: {
                        xs: `${16 + idx * 60}px !important`,
                        sm: `${24 + idx * 64}px !important`,
                    },
                }}
            >
                <Alert
                    severity={t.severity}
                    onClose={() => setDownloadToasts((cur) => cur.filter((x) => x.id !== t.id))}
                    icon={t.severity === 'info' ? <CircularProgress size={16} /> : undefined}
                    sx={{ minWidth: 280, fontWeight: 600 }}
                    elevation={6}
                >
                    {t.message}
                </Alert>
            </Snackbar>
        ))}
      </>
    );
}


function formatBytesShort(n) {
    if (!n || n < 1024) return `${n || 0} B`;
    const kb = n / 1024;
    if (kb < 1024) return `${kb.toFixed(1)} KB`;
    const mb = kb / 1024;
    if (mb < 1024) return `${mb.toFixed(1)} MB`;
    return `${(mb / 1024).toFixed(2)} GB`;
}


// ---------------------------------------------------------------------------
// FullscreenRecorder — Portal overlay covering the whole viewport while the
// recording session is live. Toolbar at the top, browser canvas filling
// the centre, captured-step list on the right.
// ---------------------------------------------------------------------------

function FullscreenRecorder({
    sessionId, phase, error, frameUrl, frameError, sessionStatus,
    steps, urlBar, setUrlBar, onUrlGo,
    onCanvasClick, onCanvasKeyDown, onCanvasWheel, imgRef,
    busyInput, viewportW, viewportH,
    workflowName, onStop, onSave, onDiscard, onCloseError,
}) {
    return (
        <Box
            tabIndex={-1}
            sx={{
                position: 'fixed',
                inset: 0,
                zIndex: 1500,    // above the parent Dialog (default 1300)
                display: 'flex',
                flexDirection: 'column',
                background: '#0f172a',
            }}
        >
            {/* Toolbar */}
            <AppBar position="static" elevation={0} sx={{
                background: '#1e293b',
                borderBottom: '1px solid rgba(255,255,255,0.08)',
            }}>
                <Toolbar sx={{ minHeight: 56, gap: 1 }}>
                    <Chip
                        size="small" color="error"
                        label="REC"
                        sx={{ fontWeight: 800, color: '#fff', bgcolor: '#dc2626' }}
                    />
                    <Typography variant="body2" sx={{
                        color: '#fff', fontWeight: 600, mr: 1,
                    }}>
                        {workflowName || 'Recorded workflow'}
                    </Typography>
                    <TextField
                        size="small"
                        className="rpa-url-input"
                        value={urlBar}
                        onChange={(e) => setUrlBar(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                onUrlGo();
                            }
                        }}
                        placeholder="https://merchant.example.com/..."
                        sx={{
                            flex: 1,
                            '& .MuiOutlinedInput-root': {
                                background: 'rgba(255,255,255,0.06)',
                                color: '#fff',
                                fontFamily: 'monospace',
                                fontSize: 13,
                                '& fieldset': { borderColor: 'rgba(255,255,255,0.12)' },
                                '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.25)' },
                            },
                        }}
                    />
                    <Button
                        onClick={onUrlGo}
                        size="small" variant="outlined"
                        disabled={busyInput || !urlBar.trim()}
                        sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.25)' }}
                    >
                        Go
                    </Button>
                    <Box sx={{ width: 12 }} />
                    <Button
                        onClick={onStop}
                        size="small" variant="outlined" color="warning"
                        disabled={phase === 'stopping'}
                    >
                        {phase === 'stopping' ? 'Stopping…' : 'Stop browser'}
                    </Button>
                    <Button
                        onClick={onSave}
                        size="small" variant="contained"
                        startIcon={<SaveIcon />}
                        disabled={steps.length === 0 || phase === 'stopping'}
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                        Save as workflow
                    </Button>
                    <Button
                        onClick={onDiscard}
                        size="small"
                        sx={{ color: 'rgba(255,255,255,0.7)' }}
                    >
                        Discard
                    </Button>
                </Toolbar>
            </AppBar>

            {error && (
                <Alert severity="error" onClose={onCloseError} sx={{ borderRadius: 0 }}>
                    {error}
                </Alert>
            )}
            {frameError && !error && (
                <Alert severity="warning" sx={{ borderRadius: 0 }}>{frameError}</Alert>
            )}


            {/* Body: canvas + step list */}
            <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
                {/* Canvas — outer flex container centres the recording frame */}
                <Box
                    tabIndex={0}
                    onKeyDown={onCanvasKeyDown}
                    onWheel={onCanvasWheel}
                    sx={{
                        flex: 1,
                        position: 'relative',
                        background: '#0f172a',
                        outline: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        p: 1.5,
                        cursor: busyInput ? 'wait' : 'crosshair',
                        '&:focus-visible': {
                            outline: 'none',
                        },
                        // Pulsing red recording border — drawn on the
                        // recording-frame Box below.
                        '@keyframes rpa-rec-pulse': {
                            '0%, 100%': {
                                borderColor: '#dc2626',
                                boxShadow: '0 0 0 1px rgba(255,255,255,0.04), '
                                         + '0 0 18px rgba(220,38,38,0.45)',
                            },
                            '50%': {
                                borderColor: '#f87171',
                                boxShadow: '0 0 0 1px rgba(255,255,255,0.04), '
                                         + '0 0 28px rgba(220,38,38,0.7)',
                            },
                        },
                        '@keyframes rpa-rec-dot': {
                            '0%, 100%': { opacity: 1, transform: 'scale(1)' },
                            '50%':       { opacity: 0.5, transform: 'scale(0.85)' },
                        },
                    }}
                >
                    {/* Recording frame — minimal styling so nothing on the
                        dashboard side can clip / mask / transform the image.
                        Only the red border + pulse animation survive; no
                        rounded corners, no overflow:hidden, no aspect-ratio
                        constraint that could interact with the <img>. */}
                    <Box sx={{
                        position: 'relative',
                        // The flex parent centres us. Take available space
                        // up to the viewport's aspect ratio so we don't
                        // squish the image.
                        maxWidth: '100%',
                        maxHeight: '100%',
                        aspectRatio: `${viewportW} / ${viewportH}`,
                        margin: 'auto',
                        // Plain rectangle with the red recording border —
                        // no border-radius, no overflow:hidden.
                        border: '4px solid #dc2626',
                        animation: 'rpa-rec-pulse 2.5s ease-in-out infinite',
                        // Defensive: hard-disable any inherited masking.
                        clipPath: 'none',
                        WebkitClipPath: 'none',
                        WebkitMaskImage: 'none',
                        maskImage: 'none',
                        filter: 'none',
                        transform: 'none',
                    }}>
                        {frameUrl ? (
                            <img
                                ref={imgRef}
                                src={frameUrl}
                                alt="Recording browser"
                                onClick={onCanvasClick}
                                draggable={false}
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    display: 'block',
                                    // 'fill' stretches to wrapper. Wrapper has
                                    // the right aspect ratio, so the image
                                    // displays at its native ratio without
                                    // letterboxing.
                                    objectFit: 'fill',
                                    userSelect: 'none',
                                    pointerEvents: busyInput ? 'none' : 'auto',
                                    background: '#fff',
                                    // Belt-and-suspenders: nothing in the
                                    // image's own styling can clip it.
                                    clipPath: 'none',
                                    WebkitClipPath: 'none',
                                    borderRadius: 0,
                                }}
                            />
                        ) : (
                            <Stack alignItems="center" justifyContent="center"
                                   spacing={1.5}
                                   sx={{ width: '100%', height: '100%',
                                         background: '#0f172a' }}>
                                <CircularProgress sx={{ color: '#94a3b8' }} />
                                <Typography variant="body2"
                                            sx={{ color: '#94a3b8' }}>
                                    Waiting for first frame…
                                </Typography>
                            </Stack>
                        )}

                        {/* ● REC indicator — top-left of the recording frame */}
                        <Box sx={{
                            position: 'absolute',
                            top: 10, left: 10,
                            display: 'flex', alignItems: 'center', gap: 0.75,
                            background: 'rgba(15,23,42,0.85)',
                            color: '#fff',
                            fontSize: 12,
                            fontWeight: 800,
                            letterSpacing: 0.6,
                            px: 1.25, py: 0.5,
                            borderRadius: 1,
                            pointerEvents: 'none',
                        }}>
                            <Box sx={{
                                width: 9, height: 9, borderRadius: '50%',
                                background: '#dc2626',
                                animation: 'rpa-rec-dot 1.4s ease-in-out infinite',
                            }} />
                            REC
                        </Box>

                        {busyInput && (
                            <Box sx={{
                                position: 'absolute', top: 10, right: 10,
                                background: 'rgba(15,23,42,0.85)',
                                color: '#fff',
                                fontSize: 11, px: 1, py: 0.25,
                                borderRadius: 1,
                                pointerEvents: 'none',
                            }}>
                                sending…
                            </Box>
                        )}
                        {sessionStatus?.frame_age_ms != null
                            && sessionStatus.frame_age_ms > 2000 && (
                            <Box sx={{
                                position: 'absolute', bottom: 10, left: 10,
                                background: 'rgba(245,158,11,0.95)',
                                color: '#fff',
                                fontSize: 11, fontWeight: 600,
                                px: 1, py: 0.5, borderRadius: 1,
                                pointerEvents: 'none',
                            }}>
                                Stale ({sessionStatus.frame_age_ms} ms) — page may be navigating
                            </Box>
                        )}

                        {/* Hint pill — bottom-right of the recording frame */}
                        <Box sx={{
                            position: 'absolute', bottom: 10, right: 10,
                            background: 'rgba(15,23,42,0.7)',
                            color: 'rgba(255,255,255,0.85)',
                            fontSize: 11,
                            px: 1.25, py: 0.5,
                            borderRadius: 1,
                            pointerEvents: 'none',
                        }}>
                            Click to interact · type to fill focused fields ·
                            Enter / Tab / arrows / scroll forwarded
                        </Box>

                        {/* No custom field overlay — date / select / etc.
                            inputs are interacted with directly on the
                            recorded page via keystroke forwarding. Click
                            into the field, then type the value (date
                            inputs accept digits in the page's locale
                            format, e.g. "05072026" for MM/DD/YYYY). */}

                        {/* Debug: open the raw PNG in a new tab. Useful when
                            the in-modal render looks wrong — if the raw PNG
                            also looks wrong, the issue is browser-side
                            (headless rendering); if it looks right, the
                            issue is dashboard-side (CSS / object-fit). */}
                        {frameUrl && (
                            <a
                                href={frameUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                    position: 'absolute',
                                    top: 10, right: 10,
                                    background: 'rgba(15,23,42,0.7)',
                                    color: 'rgba(255,255,255,0.85)',
                                    fontSize: 11,
                                    padding: '4px 10px',
                                    borderRadius: 4,
                                    textDecoration: 'none',
                                    pointerEvents: 'auto',
                                }}
                                onClick={(e) => e.stopPropagation()}
                            >
                                View raw frame ↗
                            </a>
                        )}
                    </Box>
                </Box>

                {/* Step list */}
                <Box sx={{
                    width: 320,
                    background: '#fff',
                    borderLeft: '1px solid rgba(15,23,42,0.08)',
                    display: 'flex',
                    flexDirection: 'column',
                }}>
                    <Box sx={{
                        px: 2, py: 1.25,
                        borderBottom: '1px solid rgba(15,23,42,0.08)',
                    }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                Captured steps
                            </Typography>
                            <Chip size="small" label={steps.length} />
                        </Stack>
                    </Box>
                    <Box sx={{ flex: 1, overflow: 'auto', p: 1 }}>
                        {steps.length === 0 ? (
                            <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
                                <Typography variant="body2">
                                    Steps will appear here as you click around.
                                </Typography>
                            </Box>
                        ) : (
                            <Stack spacing={0.75}>
                                {steps.map((s) => (
                                    <Paper key={s.id} variant="outlined" sx={{ p: 1 }}>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <Chip size="small" label={`#${s.step_idx + 1}`}
                                                  sx={{ minWidth: 36 }} />
                                            {ACTION_ICON[s.action]}
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                {ACTION_LABEL[s.action] || s.action}
                                            </Typography>
                                        </Stack>
                                        {s.semantic_label && (
                                            <Typography variant="caption" sx={{
                                                display: 'block',
                                                color: 'text.secondary',
                                                ml: 5,
                                            }}>
                                                {s.semantic_label}
                                            </Typography>
                                        )}
                                        {(s.selector?.primary || s.selector_used) && (
                                            <Typography variant="caption" sx={{
                                                display: 'block',
                                                fontFamily: 'monospace',
                                                color: 'text.disabled',
                                                ml: 5,
                                                wordBreak: 'break-all',
                                            }}>
                                                {s.selector?.primary || s.selector_used}
                                            </Typography>
                                        )}
                                        {s.value && s.action !== 'navigate' && (
                                            <Typography variant="caption" sx={{
                                                display: 'block',
                                                ml: 5,
                                                color: 'text.secondary',
                                            }}>
                                                value: {redactPwd(String(s.value))}
                                            </Typography>
                                        )}
                                    </Paper>
                                ))}
                            </Stack>
                        )}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}


function phaseColor(phase) {
    return ({
        idle: 'default',
        starting: 'info',
        running: 'success',
        stopping: 'warning',
        finished: 'success',
        failed: 'error',
    })[phase] || 'default';
}

function phaseLabel(phase) {
    return ({
        idle: 'Idle',
        starting: 'Starting browser…',
        running: 'Recording',
        stopping: 'Stopping…',
        finished: 'Finished',
        failed: 'Failed',
    })[phase] || phase;
}


// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function stepsForSave(steps) {
    // Strip empty selector keys to keep the JSON clean.
    return steps.map((s, idx) => {
        const sel = {};
        if (s.selector?.primary) sel.primary = s.selector.primary;
        if (s.selector?.xpath) sel.xpath = s.selector.xpath;
        if (s.selector?.text) sel.text = s.selector.text;
        const out = { step_idx: idx, action: s.action };
        if (Object.keys(sel).length) out.selector = sel;
        if (s.value !== undefined && s.value !== '') out.value = s.value;
        if (s.semantic_label) out.semantic_label = s.semantic_label;
        // Per-step relative date filter recipe — only persist if the user
        // actually picked a preset; an empty/None recipe means "use literal".
        const df = cleanStepDateFilter(s.date_filter);
        if (df) out.date_filter = df;
        return out;
    });
}


// Strip empty / half-filled recipes so the workflow JSON stays clean.
// Returns either a sanitised dict or null.
function cleanStepDateFilter(df) {
    if (!df || typeof df !== 'object') return null;
    if (!df.preset) return null;
    const out = { preset: df.preset, format: df.format || 'YYYY-MM-DD' };
    if (df.preset === 'n_days_ago') {
        out.offset_value = parseInt(df.offset_value, 10) || 0;
        out.offset_unit = df.offset_unit || 'days';
    }
    if (df.preset === 'literal') {
        out.literal = (df.literal || '').trim();
    }
    return out;
}

function validateDraft(draft, setError) {
    const requiresSelector = ['click', 'type', 'select', 'download'].includes(draft.action);
    const requiresValue = ['navigate', 'type', 'select'].includes(draft.action);
    if (!draft.action) { setError('Pick an action.'); return false; }
    if (requiresSelector && !draft.selector?.primary?.trim()) {
        setError(`The "${draft.action}" action needs a primary selector.`);
        return false;
    }
    if (requiresValue && (!draft.value || !String(draft.value).trim())) {
        setError(`The "${draft.action}" action needs a value.`);
        return false;
    }
    return true;
}

function redactPwd(value) {
    if (typeof value !== 'string') return value;
    if (value.includes('{{__password__}}')) return '••••••• (password placeholder)';
    return value.length > 80 ? value.slice(0, 80) + '…' : value;
}

// Normalise an existing workflow step (from rpaWorkflowGet) into the editor's
// internal shape. The backend may send selector as a flat string in legacy
// rows, so coerce to an object with primary/xpath/text keys.
function normaliseSeedStep(s, idx) {
    const sel = s?.selector;
    let selector = { primary: '', xpath: '', text: '' };
    if (typeof sel === 'string') {
        selector.primary = sel;
    } else if (sel && typeof sel === 'object') {
        selector = {
            primary: sel.primary || '',
            xpath: sel.xpath || '',
            text: sel.text || '',
        };
    }
    return {
        action: s?.action || 'navigate',
        selector,
        value: s?.value !== undefined ? s.value : '',
        semantic_label: s?.semantic_label || '',
        date_filter: s?.date_filter || null,
        step_idx: idx,
    };
}


// ===========================================================================
// StepDateFilter — per-step relative-date recipe picker.
//
// When attached to a step that types or selects a value, the picker turns
// the step's literal recorded value (e.g. "2026-05-05") into a recipe
// (e.g. "yesterday"). At run time the runtime resolves the recipe against
// the CURRENT clock so the bot types fresh dates every time the schedule
// fires — no need to re-record a workflow each morning.
//
// "Notify what the agent will filter": we render a live preview chip that
// shows what the recipe resolves to right now. The user sees feedback the
// instant they pick a preset, before the workflow ever runs.
// ===========================================================================

function StepDateFilter({ value, recordedLiteral, onChange }) {
    const enabled = !!(value && value.preset);
    const [presets, setPresets] = useState([]);
    const [formats, setFormats] = useState([]);
    const [resolved, setResolved] = useState(null);

    useEffect(() => {
        let cancelled = false;
        rpaDateFilterPresets()
            .then((r) => {
                if (cancelled) return;
                if (r?.data?.success) {
                    setPresets(r.data.data?.presets || []);
                    setFormats(r.data.data?.formats || []);
                }
            })
            .catch(() => { /* non-blocking */ });
        return () => { cancelled = true; };
    }, []);

    // Live-preview the recipe against server time so the user always sees
    // accurate "what would the bot type today" feedback.
    useEffect(() => {
        if (!enabled) { setResolved(null); return; }
        let cancelled = false;
        const cleaned = cleanStepDateFilter(value);
        if (!cleaned) { setResolved(null); return; }
        rpaDateFilterPreview(cleaned)
            .then((r) => {
                if (cancelled) return;
                if (r?.data?.success) {
                    // The single-side payload comes back under start_date.
                    setResolved(r.data.data?.resolved?.start_date || null);
                }
            })
            .catch(() => { if (!cancelled) setResolved(null); });
        return () => { cancelled = true; };
    }, [enabled, value]);

    const setSide = (patch) => onChange({ ...(value || {}), ...patch });
    const showOffset = value?.preset === 'n_days_ago';
    const showLiteral = value?.preset === 'literal';

    return (
        <Paper
            variant="outlined"
            sx={{ p: 1.5, borderRadius: 1.5, background: '#FAFAFC' }}
        >
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: enabled ? 1.25 : 0 }}>
                <EventIcon fontSize="small" sx={{ color: '#4F46E5' }} />
                <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        Relative date filter
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {enabled
                            ? 'Bot computes the date at run time from the recipe below.'
                            : 'Off — the bot will type the recorded literal value.'}
                    </Typography>
                </Box>
                <Button
                    size="small"
                    variant={enabled ? 'outlined' : 'contained'}
                    color={enabled ? 'inherit' : 'primary'}
                    onClick={() => onChange(enabled ? null : {
                        preset: 'yesterday',
                        format: 'YYYY-MM-DD',
                        offset_value: 1,
                        offset_unit: 'days',
                        literal: '',
                    })}
                    sx={{ textTransform: 'none' }}
                >
                    {enabled ? 'Turn off' : 'Make dynamic'}
                </Button>
            </Stack>

            {enabled && (
                <Stack spacing={1.25}>
                    <FormControl size="small" fullWidth>
                        <InputLabel>Recipe</InputLabel>
                        <Select
                            label="Recipe"
                            value={value?.preset || ''}
                            onChange={(e) => setSide({ preset: e.target.value })}
                        >
                            {presets.map((p) => (
                                <MenuItem key={p.value} value={p.value}>
                                    <Box>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            {p.label}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {p.description}
                                        </Typography>
                                    </Box>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {showOffset && (
                        <Stack direction="row" spacing={1}>
                            <TextField
                                size="small"
                                label="Offset"
                                type="number"
                                value={value?.offset_value ?? 0}
                                onChange={(e) => setSide({ offset_value: e.target.value })}
                                inputProps={{ min: 0, step: 1 }}
                                sx={{ flex: 1 }}
                            />
                            <FormControl size="small" sx={{ flex: 1 }}>
                                <InputLabel>Unit</InputLabel>
                                <Select
                                    label="Unit"
                                    value={value?.offset_unit || 'days'}
                                    onChange={(e) => setSide({ offset_unit: e.target.value })}
                                >
                                    <MenuItem value="days">Days ago</MenuItem>
                                    <MenuItem value="weeks">Weeks ago</MenuItem>
                                    <MenuItem value="months">Months ago</MenuItem>
                                    <MenuItem value="years">Years ago</MenuItem>
                                </Select>
                            </FormControl>
                        </Stack>
                    )}

                    {showLiteral && (
                        <TextField
                            size="small"
                            label="Literal value (no auto-update)"
                            value={value?.literal || ''}
                            onChange={(e) => setSide({ literal: e.target.value })}
                            placeholder="2026-01-01"
                        />
                    )}

                    {!showLiteral && (
                        <FormControl size="small" fullWidth>
                            <InputLabel>Format</InputLabel>
                            <Select
                                label="Format"
                                value={value?.format || 'YYYY-MM-DD'}
                                onChange={(e) => setSide({ format: e.target.value })}
                            >
                                {formats.map((f) => (
                                    <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}

                    {/* Live preview banner — the "notify the user what the
                        agent will filter" feedback the request asked for. */}
                    <Box sx={{
                        p: 1.25, borderRadius: 1,
                        background: 'linear-gradient(135deg, #ECFDF5 0%, #EEF2FF 100%)',
                        border: '1px solid #A7F3D0',
                    }}>
                        <Stack direction="row" alignItems="center" spacing={0.75}>
                            <Typography variant="caption" sx={{
                                fontWeight: 700, color: '#047857',
                                textTransform: 'uppercase', letterSpacing: 0.5,
                            }}>
                                Bot will type
                            </Typography>
                            <Chip
                                size="small"
                                label={resolved || 'Resolving…'}
                                sx={{
                                    height: 20,
                                    fontWeight: 700,
                                    fontFamily: 'monospace',
                                    bgcolor: '#D1FAE5',
                                    color: '#047857',
                                }}
                            />
                            <Typography variant="caption" color="text.secondary">
                                today
                            </Typography>
                        </Stack>
                        {recordedLiteral && resolved && recordedLiteral !== resolved && (
                            <Typography variant="caption" sx={{
                                display: 'block', mt: 0.5, color: 'text.secondary',
                            }}>
                                Recorded literal{' '}
                                <code style={{ fontFamily: 'monospace' }}>{redactPwd(recordedLiteral)}</code>{' '}
                                will be ignored once this recipe is saved.
                            </Typography>
                        )}
                    </Box>
                </Stack>
            )}
        </Paper>
    );
}
